import { useState, useEffect, memo, useRef, useCallback } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { SortableTableHeader, useSortableData } from "../ui/sortable-table";
import { FilterableTableHeader } from "../ui/filterable-table-header";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Edit, Trash2, Phone, PhoneCall, EyeOff, Building2, UserCheck, Users, Mail } from "lucide-react";
import { format } from "date-fns";
import { VoiceCallModal } from "./VoiceCallModal.jsx";
import { useAuth } from "../../hooks/useAuth";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { toast } from "sonner";
import { getFullName } from "../../services/common/util.js";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers } from "../../services/crmServices/userService";
import { fetchDistinctValues } from "../../services/crmServices/leadService";
import { searchPositions } from "../../services/crmServices/positionService";

export const LeadsTable = memo(function LeadsTable({
    leads,
    onSelectLead,
    isLeadSelected,
    showBulkSelection = false,
    onEdit,
    onSendEmail,
    onDelete,
    loading,
    initLeads,
    refreshCurrentPage, // New prop: refresh current page without resetting
    onLeadUpdate, // New prop: update lead in Redux without refresh
    onModalClose, // New prop: callback when modal closes (for deferred refresh)
    showStatusBadges = false, // New prop
    showDeskInfo = true, // New prop
    showAgentInfo = true, // New prop
    onCurrentPageLeadsChange, // Callback to notify parent of current page leads
    serverSidePagination = false, // New prop: if true, use server-side pagination
    pagination = null, // Pagination metadata from server
    currentPage: serverCurrentPage = 1, // Current page from parent (server-side)
    onPageChange, // Callback to change page (server-side)
    pageSize: serverPageSize = 10, // Page size from parent (server-side)
    onPageSizeChange, // Callback to change page size (server-side)
    currentFilters = {}, // Current filter values
    onFilterChange, // Callback when filters change
    useServerSort = false, // If true, rely on server-side sorting (for Leads page)
    serverSortConfig = null, // External sort config from parent (for server sort)
    onServerSortChange // Callback when a sortable header is clicked (for server sort)
}) {
    const { user } = useAuth();
    const [clientCurrentPage, setClientCurrentPage] = useState(1);
    const [clientPageSize, setClientPageSize] = useState(10);
    const [showCallModal, setShowCallModal] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const [selectedLeadIndex, setSelectedLeadIndex] = useState(-1);
    const [jumpToPageInput, setJumpToPageInput] = useState("");
    const leadStatuses = useSelector(selectLeadStatuses);
    
    // Add refs to cache positions to avoid multiple API calls
    const agentPositionsRef = useRef(null);
    const floorManagerPositionsRef = useRef(null);
    const hasLoadedFilterOptionsRef = useRef(false); // Add this to prevent duplicate calls
    
    // Add refs to track if sources/affiliates/countries have been loaded
    const sourcesLoadedRef = useRef(false);
    const affiliatesLoadedRef = useRef(false);
    const countriesLoadedRef = useRef(false);
    
    // Filter options state
    const [filterOptions, setFilterOptions] = useState({
        desks: [],
        agents: [],
        floorManagers: [],
        sources: [],
        affiliates: [],
        countries: [],
        statuses: []
    });
    
    const isAdmin = user?.position?.role === "admin";
    const isFloorManager = user?.position?.role === "floorManager";
    const isDeskManager = user?.position?.role === "deskManager";
    const canFilterByAgent = isAdmin || isFloorManager || isDeskManager;

    const {
        sortedData: localSortedData,
        sortConfig: localSortConfig,
        handleSort: handleLocalSort
    } = useSortableData(leads, "updatedAt", "desc");

    // Use server-side or client-side pagination
    const isServerSide = serverSidePagination && pagination;

    // When using server-side sorting (Leads page), rely on backend order
    const effectiveSortConfig = useServerSort ? serverSortConfig : localSortConfig;
    const handleSort = (key) => {
        if (useServerSort && onServerSortChange) {
            onServerSortChange(key);
        } else {
            handleLocalSort(key);
        }
    };

    const sortedData = useServerSort ? leads : localSortedData;
    const currentPage = isServerSide ? serverCurrentPage : clientCurrentPage;
    const pageSize = isServerSide ? serverPageSize : clientPageSize;
    const totalPages = isServerSide ? (pagination?.totalPages || 1) : Math.ceil(sortedData.length / pageSize);
    
    // Reset to page 1 when leads change significantly (e.g., after filtering) - only for client-side
    useEffect(() => {
        if (!isServerSide && currentPage > totalPages && totalPages > 0) {
            setClientCurrentPage(1);
        }
    }, [sortedData.length, totalPages, currentPage, isServerSide]);
    
    // For server-side: display all leads (already paginated by server)
    // For client-side: paginate locally
    const displayLeads = isServerSide ? sortedData : (() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedData.slice(startIndex, startIndex + pageSize);
    })();

    // Notify parent component of current page leads whenever they change
    useEffect(() => {
        if (onCurrentPageLeadsChange) {
            onCurrentPageLeadsChange(displayLeads);
        }
    }, [displayLeads, onCurrentPageLeadsChange]);

    const handlePrevious = () => {
        if (isServerSide && onPageChange) {
            onPageChange(Math.max(currentPage - 1, 1));
        } else {
            setClientCurrentPage((prev) => Math.max(prev - 1, 1));
        }
    };

    const handleNext = () => {
        if (isServerSide && onPageChange) {
            onPageChange(Math.min(currentPage + 1, totalPages));
        } else {
            setClientCurrentPage((prev) => Math.min(prev + 1, totalPages));
        }
    };

    const handleJumpToPage = () => {
        const pageNumber = parseInt(jumpToPageInput, 10);
        if (isNaN(pageNumber) || pageNumber < 1 || pageNumber > totalPages) {
            toast.error(`Please enter a valid page number between 1 and ${totalPages}`);
            return;
        }
        
        if (isServerSide && onPageChange) {
            onPageChange(pageNumber);
        } else {
            setClientCurrentPage(pageNumber);
        }
        setJumpToPageInput("");
    };

    const handleJumpToPageKeyPress = (e) => {
        if (e.key === "Enter") {
            handleJumpToPage();
        }
    };

    const handleCall = (lead) => {
        // Check if user has call provider configured
        if (
            !(user.commpeak && user.permissions.voip?.commpeak) &&
            !(user.voiso && user.permissions.voip?.voiso) &&
            !(user.coperato && user.permissions.voip?.coperato) &&
            !(user.squaretalk && user.permissions.voip?.squaretalk) &&
            !(user.octella && user.permissions.voip?.octella) &&
            !(user.ccc && user.permissions.voip?.ccc)
        ) {
            toast.error("No call provider configured. Please contact administrator to set up VoIP.");
            return;
        }

        // Find the index in displayLeads (the actual displayed/sorted data)
        const pageLeadIndex = displayLeads.findIndex(l => l._id === lead._id);
        
        // Calculate actual index considering pagination
        let actualIndex = pageLeadIndex;
        if (isServerSide && pagination) {
            // For server-side pagination: actualIndex = (currentPage - 1) * pageSize + pageLeadIndex
            actualIndex = (currentPage - 1) * pageSize + pageLeadIndex;
        }
        
        setSelectedLead(lead);
        setSelectedLeadIndex(actualIndex);
        setShowCallModal(true);
    };
    
    // Handle navigation between leads in call modal
    const handleLeadNavigate = (newLead, newIndex) => {
        setSelectedLeadIndex(newIndex);
        
        if (newLead) {
            // Lead is provided (same page navigation)
            setSelectedLead(newLead);
        } else {
            // Lead is null (cross-page navigation)
            // The lead will be updated once the new page loads via useEffect below
        }
    };
    
    // Auto-select lead when page changes and we have a pending index
    useEffect(() => {
        if (selectedLeadIndex >= 0 && !selectedLead && displayLeads.length > 0) {
            // Calculate which lead should be selected on this page
            const indexInPage = selectedLeadIndex % pageSize;
            if (indexInPage >= 0 && indexInPage < displayLeads.length) {
                setSelectedLead(displayLeads[indexInPage]);
            }
        }
    }, [displayLeads, selectedLeadIndex, selectedLead, pageSize]);
    
    // Handle lead updates - update selectedLead if it's the same lead being edited
    const handleLeadUpdateWrapper = useCallback((updatedLead) => {
        // Always call the parent's onLeadUpdate
        if (onLeadUpdate) {
            onLeadUpdate(updatedLead);
        }
        
        // If the updated lead is the currently selected lead, update it in local state
        // BUT delay the update to prevent modal from closing/reopening during save
        // This keeps the lead in the modal even if it's filtered out from the list
        if (selectedLead && updatedLead) {
            const selectedId = selectedLead._id?.toString ? selectedLead._id.toString() : selectedLead._id;
            const updatedId = updatedLead._id?.toString ? updatedLead._id.toString() : updatedLead._id;
            
            if (selectedId === updatedId) {
                // Delay the update to prevent modal re-render during save
                // Use requestAnimationFrame to defer until after current render cycle
                requestAnimationFrame(() => {
                    setSelectedLead(updatedLead);
                });
            }
        }
    }, [onLeadUpdate, selectedLead]);

    // Fetch filter options - MUST be before any early returns
    useEffect(() => {
        // Prevent duplicate calls
        if (hasLoadedFilterOptionsRef.current) {
            return;
        }

        const loadFilterOptions = async () => {
            try {
                // Mark as loading to prevent duplicate calls
                hasLoadedFilterOptionsRef.current = true;

                // STEP 1: Fetch all positions once in parallel
                const [desksResponse, agentPositionsResponse, floorManagerPositionsResponse] = await Promise.all([
                    fetchDesks(),
                    searchPositions({ role: "agent" }),
                    isAdmin ? searchPositions({ role: "floorManager" }) : Promise.resolve({ data: [] })
                ]);
                
                // Cache positions
                agentPositionsRef.current = agentPositionsResponse?.data || [];
                floorManagerPositionsRef.current = floorManagerPositionsResponse?.data || [];
                
                // Process desks
                if (desksResponse?.data?.length) {
                    let filteredDesks = desksResponse.data;
                    if (isDeskManager) {
                        filteredDesks = desksResponse.data.filter(desk => {
                            const managerId = desk.manager?._id || desk.manager;
                            return managerId?.toString() === user._id?.toString();
                        });
                    } else if (isFloorManager) {
                        filteredDesks = desksResponse.data.filter(desk => {
                            const floorManagerId = desk.floorManager?._id || desk.floorManager;
                            return floorManagerId?.toString() === user._id?.toString() && desk.floor === user.position.floor;
                        });
                    }
                    setFilterOptions(prev => ({
                        ...prev,
                        desks: filteredDesks.map(desk => ({ label: desk.name, value: desk._id }))
                    }));
                    
                    // Fetch agents if user can filter by agent (using cached positions)
                    if (canFilterByAgent && agentPositionsRef.current.length > 0) {
                        let agentsResponse;
                        if (isAdmin) {
                            agentsResponse = await searchUsers({ 
                                position: { $in: agentPositionsRef.current.map((pos) => pos._id) } 
                            });
                        } else if (isFloorManager) {
                            const managedDeskIds = filteredDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                            if (managedDeskIds.length > 0) {
                                agentsResponse = await searchUsers({ 
                                    position: { $in: agentPositionsRef.current.map((pos) => pos._id) },
                                    agentDesk: { $in: managedDeskIds }
                                });
                            }
                        } else if (isDeskManager) {
                            const managedDeskIds = filteredDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                            if (managedDeskIds.length > 0) {
                                agentsResponse = await searchUsers({ 
                                    position: { $in: agentPositionsRef.current.map((pos) => pos._id) },
                                    agentDesk: { $in: managedDeskIds }
                                });
                            }
                        }
                        if (agentsResponse?.data?.length) {
                            setFilterOptions(prev => ({
                                ...prev,
                                agents: agentsResponse.data.map(agent => ({ 
                                    label: getFullName(agent), 
                                    value: agent._id 
                                }))
                            }));
                        }
                    }
                }
                
                // Fetch floor managers (admin only) using cached positions
                if (isAdmin && floorManagerPositionsRef.current.length > 0) {
                    const floorManagersResponse = await searchUsers({
                        position: { $in: floorManagerPositionsRef.current.map((pos) => pos._id) }
                    });
                    if (floorManagersResponse?.data?.length) {
                        setFilterOptions(prev => ({
                            ...prev,
                            floorManagers: floorManagersResponse.data.map(fm => ({ 
                                label: getFullName(fm), 
                                value: fm._id 
                            }))
                        }));
                    }
                }
                
                // REMOVED: Don't fetch sources and affiliates here - fetch on demand when filter opens
                
                // Set statuses from Redux
                if (leadStatuses.length > 0) {
                    setFilterOptions(prev => ({
                        ...prev,
                        statuses: leadStatuses
                    }));
                }
            } catch (error) {
                console.error("Error loading filter options:", error);
                // Reset flag on error so it can retry
                hasLoadedFilterOptionsRef.current = false;
            }
        };
        
        loadFilterOptions();
    }, [isAdmin, isFloorManager, isDeskManager, canFilterByAgent, user._id, user.position?.floor, leadStatuses]);

    // Handle filter dropdown open/close - fetch distinct values when needed
    const handleFilterOpenChange = async (filterKey, isOpen) => {
        // Only fetch when opening and if not already loaded
        if (!isOpen) return;

        if (filterKey === 'source' && !sourcesLoadedRef.current) {
            sourcesLoadedRef.current = true;
            try {
                const sourcesResponse = await fetchDistinctValues('source');
                if (sourcesResponse?.data) {
                    setFilterOptions(prev => ({
                        ...prev,
                        sources: sourcesResponse.data.map(source => ({ label: source, value: source }))
                    }));
                }
            } catch (error) {
                console.error("Error fetching sources:", error);
                sourcesLoadedRef.current = false; // Allow retry on error
            }
        } else if (filterKey === 'affiliate' && !affiliatesLoadedRef.current) {
            affiliatesLoadedRef.current = true;
            try {
                const affiliatesResponse = await fetchDistinctValues('affiliate');
                if (affiliatesResponse?.data) {
                    setFilterOptions(prev => ({
                        ...prev,
                        affiliates: affiliatesResponse.data.map(affiliate => ({ label: affiliate, value: affiliate }))
                    }));
                }
            } catch (error) {
                console.error("Error fetching affiliates:", error);
                affiliatesLoadedRef.current = false; // Allow retry on error
            }
        } else if (filterKey === 'country' && !countriesLoadedRef.current) {
            countriesLoadedRef.current = true;
            try {
                const countriesResponse = await fetchDistinctValues('country');
                if (countriesResponse?.data) {
                    setFilterOptions(prev => ({
                        ...prev,
                        countries: countriesResponse.data.map(country => ({ label: country, value: country }))
                    }));
                }
            } catch (error) {
                console.error("Error fetching countries:", error);
                countriesLoadedRef.current = false; // Allow retry on error
            }
        }
    };

    const getCallButtonColor = () => {
        // if (user.commpeak && user.voiso) {
        //     return "text-green-600 hover:text-green-700"; // Both configured
        // } else if (user.commpeak || user.voiso) {
        //     return "text-blue-600 hover:text-blue-700"; // One configured
        // } else {
        //     return "text-gray-400"; // None configured
        // }
        return "text-green-600 hover:text-green-700";
    };

    const getCallButtonTitle = () => {
        const providers = [];
        if (user.commpeak && user.permissions.voip?.commpeak) providers.push("CommPeak");
        if (user.voiso && user.permissions.voip?.voiso) providers.push("Voiso");
        if (user.coperato && user.permissions.voip?.coperato) providers.push("Coperato");
        if (user.squaretalk && user.permissions.voip?.squaretalk) providers.push("SquareTalk");

        if (providers.length > 1) {
            return `Call via ${providers.join(" or ")}`;
        } else if (providers.length === 1) {
            return `Call via ${providers[0]}`;
        } else {
            return "No call provider configured";
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16 bg-white border border-gray-200 rounded-lg">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-blue-500 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-600 font-medium">Loading leads...</p>
                </div>
            </div>
        );
    }

    if (leads.length === 0) {
        return (
            <div className="flex items-center justify-center py-16 bg-white border border-gray-200 rounded-lg">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 text-gray-300 mb-4">
                        <Users className="h-12 w-12" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-1">No leads found</h3>
                    <p className="text-sm text-gray-500">Try adjusting your search or filter criteria</p>
                </div>
            </div>
        );
    }

    const onCloseVoiceCallModal = () => {
        setShowCallModal(false);
        // Check if refresh is needed (when lead was filtered out during save)
        // Refresh will be handled by parent component via callback
        if (onModalClose) {
            onModalClose();
        }
    };

    const handleCallSuccess = () => {
        // Only refresh when call is actually completed
        // Use refreshCurrentPage to avoid resetting to page 1
        if (refreshCurrentPage) {
            refreshCurrentPage();
        } else if (initLeads) {
            // Fallback to initLeads if refreshCurrentPage is not available
            initLeads();
        }
    };

    const getStatusConfig = (statusValue) => {
        return leadStatuses.find((status) => status.value === statusValue);
    };

    // Handle filter change
    const handleFilterChange = (filterKey, values) => {
        if (onFilterChange) {
            let newFilters = {
                ...currentFilters
            };
            
            // For filters, set directly
            newFilters[filterKey] = values.length > 0 ? values : undefined;
            
            // Remove undefined values
            Object.keys(newFilters).forEach(key => {
                if (newFilters[key] === undefined || (Array.isArray(newFilters[key]) && newFilters[key].length === 0)) {
                    delete newFilters[key];
                }
            });
            onFilterChange(newFilters);
        }
    };

    return (
        <div className="w-full">
            <div className="border border-gray-200 bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <Table className="text-sm w-full">
                        <TableHeader className="bg-gray-50">
                            <TableRow className="border-b border-gray-200">
                                {showBulkSelection && <TableHead className="w-12"></TableHead>}
                                <TableHead>Actions</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <FilterableTableHeader
                                    filterKey="statuses"
                                    options={filterOptions.statuses}
                                    selectedValues={currentFilters.statuses || []}
                                    onFilterChange={handleFilterChange}
                                    placeholder="Filter by status"
                                    className="w-[150px]"
                                >
                                    Status
                                </FilterableTableHeader>
                                {showDeskInfo && (
                                    <FilterableTableHeader
                                        filterKey="desk"
                                        options={filterOptions.desks}
                                        selectedValues={currentFilters.desk || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by desk"
                                    >
                                        Desk
                                    </FilterableTableHeader>
                                )}
                                {user.position?.role === "admin" && (
                                    <FilterableTableHeader
                                        filterKey="floorManager"
                                        options={filterOptions.floorManagers}
                                        selectedValues={currentFilters.floorManager || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by floor manager"
                                    >
                                        Floor Manager
                                    </FilterableTableHeader>
                                )}
                                {showAgentInfo && (
                                    <FilterableTableHeader
                                        filterKey="agent"
                                        options={[
                                            { label: "Unassigned", value: "unassigned" },
                                            ...filterOptions.agents
                                        ]}
                                        selectedValues={currentFilters.agent || []}
                                        onFilterChange={handleFilterChange}
                                        placeholder="Filter by agent"
                                    >
                                        Agent
                                    </FilterableTableHeader>
                                )}
                                <FilterableTableHeader
                                    filterKey="source"
                                    options={filterOptions.sources}
                                    selectedValues={currentFilters.source || []}
                                    onFilterChange={handleFilterChange}
                                    onOpenChange={handleFilterOpenChange}
                                    placeholder="Filter by source"
                                >
                                    Source
                                </FilterableTableHeader>
                                <FilterableTableHeader
                                    filterKey="affiliate"
                                    options={filterOptions.affiliates}
                                    selectedValues={currentFilters.affiliate || []}
                                    onFilterChange={handleFilterChange}
                                    onOpenChange={handleFilterOpenChange}
                                    placeholder="Filter by affiliate"
                                >
                                    Affiliate
                                </FilterableTableHeader>
                                <FilterableTableHeader
                                    filterKey="country"
                                    options={filterOptions.countries}
                                    selectedValues={currentFilters.country || []}
                                    onFilterChange={handleFilterChange}
                                    onOpenChange={handleFilterOpenChange}
                                    placeholder="Filter by country"
                                >
                                    Country
                                </FilterableTableHeader>
                                {/* Non-sortable communication columns on Leads page */}
                                <TableHead>Last Comm. Date</TableHead>
                                <TableHead>Last Comm. Staff</TableHead>
                                <TableHead>Last Comment</TableHead>
                                <TableHead className="text-center whitespace-nowrap">Calls</TableHead>
                                {user.permissions.gridLeadsColumns.registered && (
                                    <SortableTableHeader
                                        sortKey="createdAt"
                                        sortConfig={effectiveSortConfig}
                                        onSort={handleSort}
                                    >
                                        Registered
                                    </SortableTableHeader>
                                )}
                                {user.permissions.gridLeadsColumns.modified && (
                                    <SortableTableHeader
                                        sortKey="updatedAt"
                                        sortConfig={effectiveSortConfig}
                                        onSort={handleSort}
                                    >
                                        Modified
                                    </SortableTableHeader>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                        {displayLeads.map((lead) => (
                            <TableRow key={lead._id}>
                                {showBulkSelection && (
                                    <TableCell>
                                        <Checkbox
                                            checked={isLeadSelected(lead._id)}
                                            onCheckedChange={(checked) => onSelectLead(lead, checked)}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <div className="flex items-center gap-1">
                                        {user.permissions.leads.update && (
                                            <button
                                                onClick={() => onEdit(lead)}
                                                className="p-1.5 text-gray-400 hover:text-blue-500:text-blue-400 hover:bg-blue-50/60:bg-blue-900/30 rounded transition-colors"
                                                title="Edit lead"
                                            >
                                                <Edit className="h-4 w-4" />
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleCall(lead)}
                                            className="p-1.5 text-gray-400 hover:text-emerald-500:text-emerald-400 hover:bg-emerald-50/60:bg-emerald-900/30 rounded transition-colors"
                                            title={getCallButtonTitle()}
                                        >
                                            <PhoneCall className="h-4 w-4" />
                                        </button>
                                        {lead.email && (
                                            <button
                                                onClick={() => onSendEmail?.(lead)}
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                title="Send email"
                                            >
                                                <Mail className="h-4 w-4" />
                                            </button>
                                        )}

                                        {user.permissions.leads.delete && (
                                            <button
                                                onClick={() => onDelete(lead._id)}
                                                className="p-1.5 text-gray-400 hover:text-rose-500:text-rose-400 hover:bg-rose-50/60:bg-rose-900/30 rounded transition-colors"
                                                title="Delete lead"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell className="font-medium text-gray-900">
                                    <div className="flex items-center gap-1.5">
                                        <span>
                                            {lead.firstName + (lead.lastName ? " " + lead.lastName : "")}
                                        </span>
                                        {lead.isLive && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 bg-emerald-100/80 rounded" title="Live Lead">
                                                LIVE
                                            </span>
                                        )}
                                    </div>
                                </TableCell>

                                <TableCell className="max-w-48 truncate text-gray-700">
                                    {user.permissions.leads.viewEmail ? lead.email : <EyeOff className="h-4 w-4 text-gray-400" />}
                                </TableCell>
                                <TableCell>
                                    {lead.phone ? (
                                        <>
                                            {user.permissions.leads.viewPhone ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm text-gray-700">{lead.phone}</span>
                                                    <button
                                                        onClick={() => handleCall(lead)}
                                                        className="p-1 text-gray-400 hover:text-green-600:text-green-400 hover:bg-green-50:bg-green-900/30 rounded transition-colors"
                                                        title={getCallButtonTitle()}
                                                        disabled={
                                                            !(user.commpeak && user.permissions.voip?.commpeak) &&
                                                            !(user.voiso && user.permissions.voip?.voiso) &&
                                                            !(user.coperato && user.permissions.voip?.coperato) &&
                                                            !(user.squaretalk && user.permissions.voip?.squaretalk)
                                                        }
                                                    >
                                                        <Phone className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <EyeOff className="h-4 w-4 text-gray-400" />
                                            )}
                                        </>
                                    ) : (
                                        <span className="text-gray-400">-</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    {showStatusBadges && getStatusConfig(lead.status) ? (
                                        <span
                                            className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-white rounded shadow-sm"
                                            style={{ backgroundColor: getStatusConfig(lead.status)?.color }}
                                        >
                                            {getStatusConfig(lead.status)?.label || lead.status}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded">
                                            {lead.status}
                                        </span>
                                    )}
                                </TableCell>
                                {showDeskInfo && (
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            <Building2 className="h-3.5 w-3.5 text-gray-400" />
                                            <span className="text-gray-700">{lead.desk?.name || "-"}</span>
                                        </div>
                                    </TableCell>
                                )}
                                {user.position?.role === "admin" && (
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            <Users className="h-3.5 w-3.5 text-gray-400" />
                                            <span className="text-gray-700">
                                                {getFullName(lead.floorManager) ||
                                                    (lead.desk?.floorManager ? getFullName(lead.desk.floorManager) : "-")}
                                            </span>
                                        </div>
                                    </TableCell>
                                )}
                                {showAgentInfo && (
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            <UserCheck className="h-3.5 w-3.5 text-gray-400" />
                                            <span className="text-gray-700">{getFullName(lead.agent) || "Unassigned"}</span>
                                        </div>
                                    </TableCell>
                                )}
                                <TableCell className="text-gray-700">{lead.source || "-"}</TableCell>
                                <TableCell className="text-gray-700">{lead.affiliate || "-"}</TableCell>
                                <TableCell className="text-gray-700">{lead.country || "-"}</TableCell>
                                <TableCell className="text-gray-600 text-xs">
                                    {lead.lastCommDate ? format(new Date(lead.lastCommDate), "MMM dd, yyyy") : "-"}
                                </TableCell>
                                <TableCell className="text-gray-700">
                                    {lead.lastCommStaff
                                        ? lead.lastCommStaff.firstName +
                                          (lead.lastCommStaff.lastName ? " " + lead.lastCommStaff.lastName : "")
                                        : "-"}
                                </TableCell>
                                <TableCell className="max-w-32 truncate text-gray-600">{lead.lastComment || "-"}</TableCell>
                                <TableCell className="text-center">
                                    <span className="inline-flex items-center gap-1 text-gray-700">
                                        <Phone className="h-3.5 w-3.5 text-gray-400" />
                                        {lead.manualCallCount || 0}
                                    </span>
                                </TableCell>
                                {user.permissions.gridLeadsColumns.registered && (
                                    <TableCell className="text-gray-600 text-xs">{format(new Date(lead.createdAt), "MMM dd, yyyy")}</TableCell>
                                )}
                                {user.permissions.gridLeadsColumns.modified && (
                                    <TableCell className="text-gray-600 text-xs">{format(new Date(lead.updatedAt), "MMM dd, yyyy")}</TableCell>
                                )}
                            </TableRow>
                        ))}
                        </TableBody>
                    </Table>
                </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-1">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600 font-medium">Show</span>
                    <select
                        value={pageSize}
                        onChange={(e) => {
                            const newPageSize = Number(e.target.value);
                            if (isServerSide && onPageSizeChange) {
                                onPageSizeChange(newPageSize);
                                if (onPageChange) onPageChange(1);
                            } else {
                                setClientPageSize(newPageSize);
                                setClientCurrentPage(1);
                            }
                        }}
                        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500:ring-blue-400 focus:border-transparent"
                    >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                    </select>
                    <span className="text-sm text-gray-600">entries</span>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handlePrevious}
                        disabled={currentPage === 1}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white:hover:bg-gray-700"
                    >
                        Previous
                    </button>
                    <span className="text-sm text-gray-700 font-medium px-3">
                        Page {currentPage} of {totalPages}
                        {isServerSide && pagination && (
                            <span className="text-xs text-gray-500 ml-2 font-normal">
                                ({pagination.totalCount.toLocaleString()} total)
                            </span>
                        )}
                    </span>
                    <button
                        onClick={handleNext}
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50:bg-gray-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white:hover:bg-gray-700"
                    >
                        Next
                    </button>
                    <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-300">
                        <span className="text-sm text-gray-600">Go to:</span>
                        <input
                            type="number"
                            min="1"
                            max={totalPages}
                            value={jumpToPageInput}
                            onChange={(e) => setJumpToPageInput(e.target.value)}
                            onKeyPress={handleJumpToPageKeyPress}
                            placeholder="#"
                            className="w-16 border border-gray-300 rounded-md px-2 py-1.5 text-sm text-center bg-white focus:outline-none focus:ring-2 focus:ring-blue-500:ring-blue-400 focus:border-transparent"
                        />
                        <button
                            onClick={handleJumpToPage}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700:bg-blue-600 rounded-md transition-colors"
                        >
                            Go
                        </button>
                    </div>
                </div>
            </div>

            <VoiceCallModal 
                open={showCallModal} 
                onClose={() => onCloseVoiceCallModal()} 
                lead={selectedLead}
                onSuccess={handleCallSuccess}
                onLeadUpdate={handleLeadUpdateWrapper}
                filteredLeads={displayLeads}
                currentLeadIndex={selectedLeadIndex}
                onNavigate={handleLeadNavigate}
                totalLeads={pagination?.totalCount || leads.length}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={onPageChange}
            />
        </div>
    );
});
